import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import { ModalLayout } from '@/components/ui/modal-layout'
import { useAppForm } from '@/lib/forms/hook'

export interface AddSessionModalProps {
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  onAdd: (name: string) => Promise<void> | void
}

const addSessionSchema = z.object({
  newSessionName: z.string().min(1, 'กรุณาระบุชื่อเซสชัน'),
})

export function AddSessionModal({
  showModal,
  setShowModal,
  onAdd,
}: AddSessionModalProps) {
  const form = useAppForm({
    defaultValues: {
      newSessionName: '',
    },
    validators: {
      onChange: addSessionSchema,
    },
    onSubmit: async ({ value }) => {
      await onAdd(value.newSessionName)
      setShowModal(false)
    },
  })

  useEffect(() => {
    if (showModal) {
      form.reset()
    }
  }, [showModal, form])

  return (
    <form.Subscribe selector={(state) => state.values.newSessionName}>
      {(newSessionName) => (
        <ModalLayout
          open={showModal}
          onOpenChange={setShowModal}
          title="เพิ่มเซสชันใหม่"
          description="ระบุชื่อเซสชันใหม่ที่ต้องการสุ่มผู้ชนะ"
          confirmText="เพิ่มเซสชัน"
          isConfirmDisabled={newSessionName.trim() === ''}
          onConfirm={() => {
            form.handleSubmit()
          }}
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <form.AppField name="newSessionName">
            {(field) => (
              <field.TextField
                label="ชื่อเซสชัน"
                placeholder="เช่น รางวัลที่ 2, รางวัลพิเศษ"
                className="h-12 rounded-full border-rose-200 bg-rose-50/10 px-5 font-bold focus-visible:border-rose-300 focus-visible:ring-2 focus-visible:ring-rose-500/20 focus-visible:ring-offset-0"
                autoFocus
              />
            )}
          </form.AppField>
        </ModalLayout>
      )}
    </form.Subscribe>
  )
}

export function useAddSessionModal() {
  const [showAddSessionModal, setShowAddSessionModal] = useState(false)

  const AddSessionModalCallback = useCallback(
    ({ onAdd }: { onAdd: (name: string) => Promise<void> | void }) => {
      return (
        <AddSessionModal
          showModal={showAddSessionModal}
          setShowModal={setShowAddSessionModal}
          onAdd={onAdd}
        />
      )
    },
    [showAddSessionModal],
  )

  return useMemo(
    () => ({
      setShowAddSessionModal,
      AddSessionModal: AddSessionModalCallback,
    }),
    [setShowAddSessionModal, AddSessionModalCallback],
  )
}
